/**
 * labMerge — collapse several lab files that are one draw into a single document,
 * and recover a merge that was interrupted partway through.
 *
 * See xdesign_docs/MULTI_DATE_LAB_INGEST_AND_MERGE.md §5.1–5.3.
 *
 * Two things live here, and they are deliberately separate:
 *
 *   mergeTestBodies / mergeDrawGroup   compose the merged document (pure)
 *   reconcileMerges                    make an interrupted merge safe (pure)
 *
 * Neither touches the network. The caller persists: upload the merged document
 * FIRST, then delete the sources. That order is what makes recovery possible —
 * the merged document's `merged_from` is the record that the sources are
 * redundant, so its existence is what authorizes their deletion. Crashing before
 * the upload leaves the sources untouched; crashing after it leaves duplicates
 * that the next load reconciles. Never the reverse, which would lose data.
 *
 * Merging never spans dates (§5.1 rule 2). A merge that averaged across draws
 * would be a destructive rollup — the user's history silently collapsed — so
 * mergeDrawGroup rejects it rather than warning about it.
 */

import { UnitConverter, normalizeUnit } from '../../lambda/shared-utils/unit-conversion.mjs';
import { parseResultValue, resultPrecision } from '../../lambda/shared-utils/normalization.mjs';

/** Header field naming the source keys a merged document replaced. */
export const MERGED_FROM = 'merged_from';

export const MERGE_STRATEGIES = ['last', 'first', 'average', 'min', 'max'];

const DEFAULT_STRATEGY = 'last';

// ---------------------------------------------------------------------------
// Marker identity and value shape
// ---------------------------------------------------------------------------

/**
 * Canonical key for a marker. Two entries collide only if they resolve to the
 * same key, so a weak name resolver produces a union rather than a bad merge.
 */
export function markerKey(entry, normalizeTestName) {
    const raw = entry?.alias || entry?.test_name;
    if (!raw) return null;
    const resolved = normalizeTestName ? normalizeTestName(raw) : raw;
    return String(resolved || raw).trim().toLowerCase();
}

/**
 * The comparator a result carries, normalized to '<' / '>' / ''.
 *
 * "<5.7" and "5.9" are not two measurements of the same thing — one is a
 * detection limit — so they must never be averaged together (§5.3). Comparing
 * prefixes is how we detect that.
 */
export function comparatorPrefix(raw) {
    if (raw == null) return '';
    const str = String(raw).trim();
    if (/^(?:<|≤|less\s+than)/i.test(str)) return '<';
    if (/^(?:>|≥|greater\s+than)/i.test(str)) return '>';
    return '';
}

/**
 * S3's write time for a file, in ms, or null when we cannot date it.
 *
 * Deliberately NOT the `merged_at` in a merged document's header: that is the
 * browser's clock, while lastModified is S3's. Comparing the two would let clock
 * skew decide whether a corrected result survives. Both sides of every
 * comparison here come from S3 or neither does.
 */
function fileTime(file) {
    const raw = file?.lastModified;
    if (!raw) return null;
    const ms = new Date(raw).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/**
 * Was this source written after the merged document that claims it?
 *
 * A source older than the merge is what it looks like: debris from a merge that
 * was interrupted between uploading the merged document and deleting its
 * sources. A source NEWER than the merge is not debris — it is a result that
 * arrived after we merged. Because the ingest lambda names documents from a
 * digest of their marker NAMES, an amended report (same panel, same tests, one
 * corrected value) lands on the exact key the merge already absorbed, so the two
 * cases are indistinguishable without a timestamp.
 *
 * Undateable means newer. Never delete a file we cannot place in time.
 *
 * A tie counts as newer for the same reason. S3 reports LastModified at SECOND
 * resolution, so "equal" is a one-second window, not an instant - and a merge
 * only runs four seconds after the file list goes quiet, so a genuine source
 * always lands in an earlier second than the document that absorbs it. That
 * makes a tie evidence of a same-second re-upload, and the fail-safe reading of
 * an ambiguous timestamp is the one that does not delete a patient's result.
 */
function isNewerThanMerge(source, mergedFile) {
    const sourceAt = fileTime(source);
    const mergedAt = fileTime(mergedFile);
    if (sourceAt === null || mergedAt === null) return true;
    return sourceAt >= mergedAt;
}

function numericValue(entry) {
    const value = parseResultValue(entry?.result);
    return Number.isFinite(value) ? value : null;
}

function rangesAgree(a, b) {
    return a?.lab_low === b?.lab_low && a?.lab_high === b?.lab_high;
}

// ---------------------------------------------------------------------------
// Per-marker reconciliation
// ---------------------------------------------------------------------------

/**
 * Convert an occurrence's result into `targetUnits`.
 *
 * @returns {{ value: number|null, converted: boolean, convertible: boolean }}
 *   `convertible` false means the two units do not reconcile — a conflict, never
 *   something to average through (§5.3).
 */
function toTargetUnits(occurrence, targetUnits, testName) {
    const value = numericValue(occurrence.entry);
    const units = occurrence.entry?.units;

    if (value === null) return { value: null, converted: false, convertible: false };
    if (!units || !targetUnits) return { value, converted: false, convertible: true };
    if (normalizeUnit(units) === normalizeUnit(targetUnits)) {
        return { value, converted: false, convertible: true };
    }

    const converted = UnitConverter.convert(testName, value, units, targetUnits);
    if (converted === null) return { value: null, converted: false, convertible: false };
    return { value: converted, converted: true, convertible: true };
}

function pickByStrategy(occurrences, strategy, targetUnits, testName) {
    if (strategy === 'first') return occurrences[0];
    if (strategy === 'last') return occurrences[occurrences.length - 1];

    // min / max compare on a common scale, so unconvertible occurrences drop out
    // of the comparison rather than being ranked against a different unit.
    const comparable = occurrences
        .map(o => ({ o, ...toTargetUnits(o, targetUnits, testName) }))
        .filter(c => c.convertible && c.value !== null);
    if (comparable.length === 0) return occurrences[occurrences.length - 1];

    const best = comparable.reduce((a, b) => {
        if (strategy === 'min') return b.value < a.value ? b : a;
        return b.value > a.value ? b : a;
    });
    return best.o;
}

/**
 * Collapse several observations of one marker into a single body entry.
 *
 * @returns {{ entry: Object, conflict: Object|null }}
 *   An entry is always produced so the merge yields a complete document; a
 *   conflict alongside it means the choice was not derivable and the UI must
 *   surface it. Nothing is guessed silently.
 */
function reconcileMarker(occurrences, { strategy, override, testName, recencyOrdered }) {
    // An explicit per-marker choice ends the question.
    if (override) {
        const chosen = occurrences.find(o => o.sourceKey === override);
        if (chosen) return { entry: { ...chosen.entry }, conflict: null };
    }

    const values = occurrences.map(o => o.entry?.result);
    const allIdentical = values.every(v => String(v ?? '') === String(values[0] ?? ''));
    const sameUnits = occurrences.every(o =>
        normalizeUnit(o.entry?.units) === normalizeUnit(occurrences[0].entry?.units));

    // The overwhelmingly common case for panel sheets from one draw: the same
    // marker repeated verbatim. Nothing to decide.
    if (allIdentical && sameUnits) {
        return { entry: { ...occurrences[0].entry }, conflict: null };
    }

    const prefixes = new Set(occurrences.map(o => comparatorPrefix(o.entry?.result)));
    const sources = occurrences.map(o => ({
        sourceKey: o.sourceKey,
        result: o.entry?.result ?? null,
        units: o.entry?.units ?? null,
        lab_low: o.entry?.lab_low ?? null,
        lab_high: o.entry?.lab_high ?? null,
    }));

    const winner = pickByStrategy(occurrences, strategy, occurrences[occurrences.length - 1].entry?.units, testName);
    const targetUnits = winner.entry?.units;

    const converted = occurrences.map(o => ({ o, ...toTargetUnits(o, targetUnits, testName) }));
    const unconvertible = converted.filter(c => !c.convertible);

    if (unconvertible.length > 0) {
        // Units that do not reconcile are a conflict, not an average (§5.3).
        return {
            entry: { ...winner.entry },
            conflict: { marker: testName, type: 'units', chosen: winner.sourceKey, sources },
        };
    }

    if (prefixes.size > 1) {
        // "<5.7" and "5.9" measure different things; picking one is a decision
        // the user has to make. Checked BEFORE the recency short-circuit below:
        // a detection limit replaced by a number is not an amended value, and
        // §5.3 says those never reconcile silently however they are ordered.
        return {
            entry: { ...winner.entry },
            conflict: { marker: testName, type: 'comparator', chosen: winner.sourceKey, sources },
        };
    }

    // Every source is dated and they are ordered oldest to newest, so "last" is
    // the most recently written report rather than an arbitrary pick. A value
    // that changed is then a corrected result, not a disagreement to escalate —
    // which is the whole point of dating them. Units that do not reconcile are
    // still a conflict above: those signal two different measurements sharing an
    // alias (RDW-CV vs RDW-SD), not an amendment.
    if (recencyOrdered && strategy === 'last') {
        return { entry: { ...winner.entry }, conflict: null };
    }

    if (strategy !== 'average') {
        return {
            entry: { ...winner.entry },
            conflict: { marker: testName, type: 'value', chosen: winner.sourceKey, sources },
        };
    }

    // --- average -----------------------------------------------------------
    const usable = converted.filter(c => c.value !== null);
    if (usable.length === 0) {
        return {
            entry: { ...winner.entry },
            conflict: { marker: testName, type: 'value', chosen: winner.sourceKey, sources },
        };
    }

    const prefix = [...prefixes][0];
    const decimals = Math.max(...occurrences.map(o => resultPrecision(o.entry?.result)));
    const mean = usable.reduce((sum, c) => sum + c.value, 0) / usable.length;

    // Reference ranges are never averaged. Carry them only when every
    // contributing source agrees; otherwise drop them rather than invent one.
    const allRangesAgree = occurrences.every(o => rangesAgree(o.entry, occurrences[0].entry));
    const entry = {
        ...winner.entry,
        result: `${prefix}${mean.toFixed(decimals)}`,
        units: targetUnits ?? winner.entry?.units ?? null,
        lab_low: allRangesAgree ? occurrences[0].entry?.lab_low ?? null : null,
        lab_high: allRangesAgree ? occurrences[0].entry?.lab_high ?? null : null,
        ref: allRangesAgree ? occurrences[0].entry?.ref ?? null : null,
    };

    return {
        entry,
        conflict: allRangesAgree
            ? null
            : { marker: testName, type: 'range', chosen: null, sources },
    };
}

// ---------------------------------------------------------------------------
// Body merge
// ---------------------------------------------------------------------------

/**
 * Merge the bodies of several source files into one.
 *
 * Ordering is the `sources` array order, and the caller owns it — "first" and
 * "last" mean whatever the caller put there. `mergeDrawGroup` seeds that order
 * from S3 `lastModified` when every source carries one, so through that entry
 * point "last" is the most recently written report.
 *
 * @param {Array} sources - file objects shaped { key, data: { body } }
 * @param {Object} [options]
 * @param {string} [options.strategy='last'] - one of MERGE_STRATEGIES
 * @param {Object} [options.overrides] - { [markerKey]: sourceKey } escape hatch
 * @param {Function} [options.normalizeTestName] - canonical-name resolver
 * @param {boolean} [options.recencyOrdered=false] - the caller guarantees
 *        `sources` is ordered oldest to newest AND that a later value supersedes
 *        an earlier one. With `strategy: 'last'` this resolves a changed value
 *        silently instead of raising a `value` conflict, so pass it ONLY for a
 *        draw known to contain an amended report — never for panel sheets from
 *        one upload burst, whose write order is arbitrary. Unit and comparator
 *        disagreements still escalate regardless.
 * @returns {{ body: Array, conflicts: Array }}
 */
export function mergeTestBodies(sources, options = {}) {
    const {
        strategy = DEFAULT_STRATEGY,
        overrides = {},
        normalizeTestName,
        recencyOrdered = false,
    } = options;

    if (!MERGE_STRATEGIES.includes(strategy)) {
        throw new Error(`Unknown merge strategy '${strategy}'`);
    }
    if (!Array.isArray(sources) || sources.length === 0) {
        return { body: [], conflicts: [] };
    }

    // Preserve first-seen order so a merged document reads like its sources.
    const order = [];
    const byMarker = new Map();
    const unnamed = [];

    for (const source of sources) {
        const entries = source?.data?.body;
        if (!Array.isArray(entries)) continue;

        for (const entry of entries) {
            const key = markerKey(entry, normalizeTestName);
            // An entry with no resolvable name cannot be deduped against
            // anything — keep it rather than drop it.
            if (!key) { unnamed.push({ ...entry }); continue; }

            if (!byMarker.has(key)) {
                byMarker.set(key, []);
                order.push(key);
            }
            byMarker.get(key).push({ entry, sourceKey: source.key });
        }
    }

    const body = [];
    const conflicts = [];

    for (const key of order) {
        const occurrences = byMarker.get(key);

        if (occurrences.length === 1) {
            body.push({ ...occurrences[0].entry });
            continue;
        }

        const testName = occurrences[0].entry?.alias
            || occurrences[0].entry?.test_name
            || key;

        const { entry, conflict } = reconcileMarker(occurrences, {
            strategy,
            override: overrides[key],
            testName,
            recencyOrdered,
        });

        body.push(entry);
        if (conflict) conflicts.push(conflict);
    }

    return { body: [...body, ...unnamed], conflicts };
}

// ---------------------------------------------------------------------------
// Draw-level merge
// ---------------------------------------------------------------------------

function unionPanels(sources) {
    const panels = [];
    for (const source of sources) {
        const raw = source?.data?.header?.panels;
        for (const name of Array.isArray(raw) ? raw : (raw ? [raw] : [])) {
            if (!panels.includes(name)) panels.push(name);
        }
    }
    return panels;
}

/**
 * Build the merged document for one draw.
 *
 * @param {Array} sources - two or more files sharing a testdate
 * @param {Object} [options] - as mergeTestBodies, plus { now } for tests
 * @returns {{ data: Object, sourceKeys: string[], conflicts: Array }}
 * @throws if the sources do not all share one testdate (§5.1 rule 2)
 */
export function mergeDrawGroup(sources, options = {}) {
    if (!Array.isArray(sources) || sources.length < 2) {
        throw new Error('A merge needs at least two source files');
    }

    const dates = new Set(sources.map(s => String(s?.data?.header?.testdate ?? '')));
    if (dates.size > 1) {
        // Averaging across draws is a destructive rollup, not a merge.
        throw new Error(`Refusing to merge across dates: ${[...dates].join(', ')}`);
    }
    if (dates.has('')) {
        // Every undated source stringifies to '', so dates.size === 1 says nothing
        // about whether these files are one draw. Without a date there is no
        // evidence they are, and fusing two genuinely different draws deletes the
        // originals - so refuse rather than guess.
        throw new Error('Refusing to merge files with no draw date');
    }

    const sourceKeys = sources.map(s => s.key).filter(Boolean);
    if (sourceKeys.length !== sources.length) {
        throw new Error('Every source must have a key to be recorded in merged_from');
    }

    // Order oldest to newest when S3 dated every source, so "first" and "last"
    // mean what they say. Without timestamps the order is the caller's, which is
    // the group's key sort - a content hash, and therefore arbitrary.
    const dated = sources.every(s => fileTime(s) !== null);
    const ordered = dated
        ? [...sources].sort((a, b) => fileTime(a) - fileTime(b))
        : sources;

    // Letting the newest value win silently is only correct when the newer file
    // is genuinely a later report. Panel sheets from one upload burst land
    // milliseconds apart in arbitrary order, and the one that happened to write
    // last is not the more correct reading - those must still raise a conflict.
    // Only the caller knows which case this is, so it opts in; reconcileMerges
    // reports it as outdatedMerges.
    const recencyOrdered = dated && options.recencyOrdered === true;

    const { body, conflicts } = mergeTestBodies(ordered, { ...options, recencyOrdered });
    const base = ordered[ordered.length - 1];

    // testtime: keep an explicit time if exactly one is present across the
    // sources. Two different explicit times never group together in the first
    // place, so this only absorbs nulls.
    const times = [...new Set(ordered
        .map(s => s?.data?.header?.testtime)
        .filter(t => t != null && t !== ''))];

    return {
        data: {
            ...base.data,
            header: {
                ...base.data?.header,
                testtime: times.length === 1 ? times[0] : (base.data?.header?.testtime ?? null),
                panels: unionPanels(ordered),
                [MERGED_FROM]: sourceKeys,
                merged_at: (options.now || new Date()).toISOString(),
            },
            body,
        },
        sourceKeys,
        conflicts,
    };
}

/**
 * Short, stable fingerprint of a document's marker set. It is what stops two
 * files for one draw from colliding on a name built only from date and time.
 *
 * NOT interchangeable with marker_digest() in the upload lambda, which is SHA-1
 * over a de-duplicated set. This is FNV-1a because it has to be synchronous in
 * the browser (SubtleCrypto is not), so the two never agree on any input.
 * Nothing requires them to: merged documents carry a `_merged_` segment the
 * lambda never emits, so the two namespaces cannot collide. Do not write code
 * that predicts one from the other.
 *
 * Set semantics are matched deliberately - trim and de-duplicate as the Python
 * side does - so that re-merging identical content lands on the same name and
 * overwrites itself rather than accumulating copies.
 */
export function markerDigest(body) {
    const names = [...new Set((Array.isArray(body) ? body : [])
        .map(e => String(e?.alias || e?.test_name || '').trim().toLowerCase())
        .filter(Boolean))]
        .sort()
        .join('|');

    // FNV-1a, 32-bit. Not a security hash — just a stable spread over one draw's
    // handful of documents, and synchronous (SubtleCrypto is not).
    let hash = 0x811c9dc5;
    for (let i = 0; i < names.length; i++) {
        hash ^= names.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0').slice(0, 6);
}

/**
 * Reduce a header field to something safe to interpolate into an S3 key.
 *
 * Mirrors _key_safe() in lambda/upload-labtest/build_labtest_script.py, and the
 * two are held to that claim by lambda/shared-utils/tests/key-safe-cases.json,
 * which both suites run. They had already drifted on whitespace before the table
 * existed - a comment asserting parity is not a mechanism.
 */
export function keySafe(value, fallback) {
    const text = String(value ?? '').trim()
        .replace(/\\/g, '/')
        .split('/').pop()
        .replace(/\s+/g, '_')
        .replace(/^\.+|\.+$/g, '');
    return text || fallback;
}

/**
 * Name a merged document the way the upload lambda names an ingested one.
 *
 * The time component matters more than it looks: getFileTestTime() falls back to
 * reading `_blood_<date>_<HH:MM>` out of the key when a header carries no
 * testtime, so writing anything other than the real collection time invents one.
 * "00:00" is the placeholder normalizeTestTime() reads back as absent.
 */
export function mergedFileName(header, body) {
    // Header fields originate in a model's parse of an uploaded document, so they
    // are reduced to a basename before going anywhere near a key - the same
    // reduction create_file_name and upload-file apply. A name carrying a path
    // separator would otherwise produce a key that every delete path collapses
    // onto some other document's basename.
    const last = keySafe(header?.last_name, 'unknown');
    const first = keySafe(header?.first_name, 'unknown');
    const date = keySafe(header?.testdate, 'nodate');
    const time = keySafe(header?.testtime, '00:00');

    return `${last}_${first}_blood_${date}_${time}_merged_${markerDigest(body)}.json`;
}

// ---------------------------------------------------------------------------
// Candidate detection
// ---------------------------------------------------------------------------

/**
 * Find draws made of more than one file.
 *
 * Takes draws that are ALREADY grouped — the same array the pills render from,
 * so a merge candidate is exactly what the UI shows as one pill and the two
 * cannot disagree about what a draw is. Files with different explicit times are
 * already in different groups, so a cross-time merge is never suggested.
 *
 * This used to call groupFilesByDraw itself, which silently produced nothing
 * when handed grouped input: each date held one group object, every sourceKeys
 * collapsed to length 1, and the filter below discarded all of them. The unit
 * tests fed it raw files and passed while the chip never once rendered. Feed it
 * the same shape the component does.
 *
 * @param {Array} draws grouped draws, from groupFilesByDraw
 * @returns {Array} the subset with more than one source, each carrying
 *   lowConfidence — true when the group absorbed an untimed file into a timed
 *   one, which is a guess about identity rather than a fact.
 */
export function findMergeCandidates(draws) {
    if (!Array.isArray(draws)) return [];

    return draws
        .filter(draw => (draw.sourceKeys?.length ?? 0) > 1)
        .map(draw => {
            const times = (draw.sourceFiles || []).map(f => f?.data?.header?.testtime ?? null);
            const hasTimed = times.some(t => t != null && t !== '');
            const hasUntimed = times.some(t => t == null || t === '');
            return { ...draw, lowConfidence: hasTimed && hasUntimed };
        });
}

// ---------------------------------------------------------------------------
// Crash recovery
// ---------------------------------------------------------------------------

function bodyMarkerKeys(data, normalizeTestName) {
    const keys = new Set();
    for (const entry of Array.isArray(data?.body) ? data.body : []) {
        const key = markerKey(entry, normalizeTestName);
        if (key) keys.add(key);
    }
    return keys;
}

/**
 * Make an interrupted merge safe, before deciding whether to finish it.
 *
 * A merged document names its sources in `merged_from`. If those sources are
 * still present, the merge was interrupted between the upload and the deletes —
 * so both copies of every marker are in the working set, and anything that
 * flattens bodies across files (`allTests`) would count them twice.
 *
 * Hiding the sources is therefore the *correctness* step and deleting them is
 * only housekeeping: the view is right the moment files load, whether or not the
 * deletes ever succeed.
 *
 * A merged document is trusted only if it actually contains what it claims to
 * replace. If the merged upload was itself truncated, the file that gets dropped
 * is the merged one — never the real data it failed to absorb.
 *
 * @param {Array} files - every file in the working set, each shaped
 *        { key, lastModified, data }
 * @param {Function} [normalizeTestName] - canonical-name resolver
 * @returns {{ visible: Array, staleSources: string[], rejectedMerges: string[],
 *        outdatedMerges: string[] }} `visible` is what the UI should render;
 *        `staleSources` are safe to delete; `rejectedMerges` are merged documents
 *        too incomplete to trust, hidden in favour of their sources;
 *        `outdatedMerges` are merged documents a newer source has overtaken —
 *        nothing is hidden or deleted for those, and a caller re-merging one
 *        should pass `recencyOrdered` so the newer value wins.
 */
export function reconcileMerges(files, normalizeTestName) {
    if (!Array.isArray(files)) return { visible: [], staleSources: [], rejectedMerges: [], outdatedMerges: [] };

    const byKey = new Map(files.map(f => [f.key, f]));
    const supersededBy = new Map();   // source key -> merged key
    const rejectedMerges = [];
    const supersededByNewerSource = [];   // merged keys whose claim is out of date

    for (const file of files) {
        const claimed = file?.data?.header?.[MERGED_FROM];
        if (!Array.isArray(claimed) || claimed.length === 0) continue;

        // Never let a merged document supersede itself. mergedFileName is a digest
        // of the body, so re-merging a draw whose marker set did not change lands
        // on a key already in merged_from - its own. Left in, the coverage check
        // below compares the file against itself, trivially passes, and marks the
        // only surviving copy of the draw stale for deletion.
        const present = claimed.filter(key => key !== file.key && byKey.has(key));
        if (present.length === 0) continue;   // steady state: sources already gone

        // Trustworthiness first, and independent of any timestamp: every marker a
        // source carries must actually be in the merged body. Asking this before
        // the recency question matters, because an undateable merged document
        // would otherwise skip the check entirely and keep its claim.
        const mergedMarkers = bodyMarkerKeys(file.data, normalizeTestName);
        const covered = present.filter(key => {
            const source = byKey.get(key);
            const sourceMarkers = bodyMarkerKeys(source?.data, normalizeTestName);
            if (sourceMarkers.size === 0) return true;   // nothing to lose
            return [...sourceMarkers].every(m => mergedMarkers.has(m));
        });

        if (covered.length !== present.length) {
            // The merged document is incomplete — distrust it, not the sources.
            rejectedMerges.push(file.key);
            continue;
        }

        // Then: is a present source debris, or a newer result? A source written
        // after this merge is a correction. Hold the whole claim, leaving every
        // source visible so the next pass folds the newer values in rather than
        // hiding and deleting them. Releasing only the newer one would still
        // delete the older siblings this merged document is the sole copy of.
        if (present.some(key => isNewerThanMerge(byKey.get(key), file))) {
            supersededByNewerSource.push(file.key);
            continue;
        }

        for (const key of covered) supersededBy.set(key, file.key);
    }

    const rejected = new Set(rejectedMerges);
    const visible = files.filter(f => !supersededBy.has(f.key) && !rejected.has(f.key));

    return {
        visible,
        staleSources: [...supersededBy.keys()],
        rejectedMerges,
        // Merged documents a newer source has overtaken. Nothing is hidden or
        // deleted for these; they are reported so a caller can say why a draw is
        // briefly showing more than one file.
        outdatedMerges: supersededByNewerSource,
    };
}

/**
 * Whether a draw should be collapsed on its own right now, and why not if it
 * should not.
 *
 * Pulled out of the effect that calls it so every refusal below is testable
 * without a React tree, a fake clock, or a way to fake an in-flight upload. The
 * reason string exists because this runs with no UI whatsoever: when it does
 * nothing, that has to be distinguishable from it never having run.
 *
 * @param {Array} draws grouped draws, from groupFilesByDraw
 * @param {Object} opts
 * @param {boolean} opts.uploadsInFlight an upload is still running: a burst of
 *   panel sheets can leave gaps longer than any settle window, and a merge
 *   landing between two of them would be redone when the next file arrives
 * @param {boolean} opts.readOnly viewing someone else's data
 * @param {Set<string>} opts.attempted source-key signatures already tried, so a
 *   refused or failed merge is not retried on every change
 */
export function autoMergeDecision(draws, { uploadsInFlight = false, readOnly = false, namesReady = true, attempted } = {}) {
    if (readOnly) return { candidate: null, reason: 'read-only view' };
    if (!namesReady) return { candidate: null, reason: 'test-name map has not loaded' };
    if (uploadsInFlight) return { candidate: null, reason: 'an upload is still in flight' };
    if (!Array.isArray(draws) || draws.length === 0) return { candidate: null, reason: 'no draws loaded yet' };

    const shape = draws.map(d => `${d.testdate}${d.testtime ? '@' + d.testtime : ''}:${d.sourceKeys?.length ?? 0}`).join(' ');
    const fragmented = findMergeCandidates(draws);
    if (fragmented.length === 0) {
        return { candidate: null, reason: `every draw is already a single file (${shape})` };
    }

    const candidate = fragmented.find(draw =>
        !draw.lowConfidence && !attempted?.has(autoMergeSignature(draw)));
    if (!candidate) {
        const why = fragmented.map(d =>
            `${d.testdate}:${d.lowConfidence ? 'times differ' : 'already attempted'}`).join(', ');
        return { candidate: null, reason: `nothing eligible (${why})` };
    }

    return { candidate, reason: `${candidate.sourceKeys.length} files from ${candidate.testdate}` };
}

/**
 * The one draw that should be collapsed on its own right now, or null.
 * Thin wrapper over autoMergeDecision for callers that do not need the reason.
 */
export function nextAutoMergeCandidate(draws, options) {
    return autoMergeDecision(draws, options).candidate;
}

/** Identity for the retry guard: the sources, not the group key. */
export function autoMergeSignature(draw) {
    return [...(draw?.sourceKeys || [])].sort().join('|');
}
